/**
 * Workspace theme picker — Settings → Workspace. Owners/admins can pick a
 * preset palette (Blue, Mint, Amber, Rose, Slate) or hand-pick two hex
 * values via "Custom". Writes to PUT /api/tenant/theme; the result is
 * surfaced on `tenant.theme_row_hover` / `tenant.theme_accent` and applied
 * app-wide by ThemeApplier via CSS variables.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Palette, Check, Loader2 } from "lucide-react";

const DISPLAY_ORDER = ["blue", "mint", "amber", "rose", "slate"];

export default function ThemePickerCard() {
  const { user, refreshMe } = useAuth();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [theme, setTheme] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customHover, setCustomHover] = useState("#EFF6FF");
  const [customAccent, setCustomAccent] = useState("#DBEAFE");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/theme");
      setTheme(data);
      if (data?.preset === "custom") {
        if (data.row_hover) setCustomHover(data.row_hover);
        if (data.accent) setCustomAccent(data.accent);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyPreset = async (presetKey) => {
    if (!canManage) return;
    setBusy(true);
    try {
      await api.put("/tenant/theme", { preset: presetKey });
      toast.success(presetKey ? `Theme set to ${presetKey}` : "Theme reset to default");
      await Promise.all([load(), refreshMe?.()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save theme");
    } finally { setBusy(false); }
  };

  const applyCustom = async () => {
    if (!canManage) return;
    setBusy(true);
    try {
      await api.put("/tenant/theme", {
        preset: "custom",
        row_hover: customHover,
        accent: customAccent,
      });
      toast.success("Custom theme applied");
      await Promise.all([load(), refreshMe?.()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save theme");
    } finally { setBusy(false); }
  };

  if (!theme) return null;

  const activePreset = theme.preset || null;

  // Live-preview a palette on hover by writing the same CSS variables
  // ThemeApplier uses. On mouse-leave we restore to the saved theme so
  // nothing persists until Apply is clicked.
  const previewPalette = (rowHover, accent) => {
    const root = document.documentElement;
    root.style.setProperty("--ll-row-hover", rowHover);
    root.style.setProperty("--ll-accent-soft", accent);
    root.style.setProperty("--ll-mint", accent);
  };
  const restorePalette = () => {
    const root = document.documentElement;
    root.style.setProperty("--ll-row-hover", theme.row_hover || "#EFF6FF");
    root.style.setProperty("--ll-accent-soft", theme.accent || "#DBEAFE");
    if (theme.accent) root.style.setProperty("--ll-mint", theme.accent);
    else root.style.removeProperty("--ll-mint");
  };

  return (
    <div className="ll-card-flat p-5 mb-6" data-testid="theme-picker-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-1.5">
            <Palette size={11} /> Workspace theme
          </div>
          <div className="font-display font-bold text-xl mt-1">Row hover & accent color</div>
          <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-prose">
            Pick a preset palette or set your own hex values. Applied instantly to table row hovers and soft accents across the workspace.
          </p>
        </div>
        {activePreset && (
          <button
            type="button"
            onClick={() => applyPreset(null)}
            disabled={busy || !canManage}
            data-testid="theme-reset-btn"
            className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-row-hover)] disabled:opacity-60"
            style={{ borderColor: "var(--ll-border)" }}
          >
            Reset to default
          </button>
        )}
      </div>

      {/* Preset swatches */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {DISPLAY_ORDER.map((key) => {
          const p = theme.presets?.[key];
          if (!p) return null;
          const active = activePreset === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => applyPreset(key)}
              onMouseEnter={() => previewPalette(p.row_hover, p.accent)}
              onMouseLeave={restorePalette}
              onFocus={() => previewPalette(p.row_hover, p.accent)}
              onBlur={restorePalette}
              disabled={busy || !canManage}
              data-testid={`theme-preset-${key}`}
              className="group relative p-3 border text-left transition-colors disabled:opacity-60"
              style={{
                borderColor: active ? "var(--ll-ink)" : "var(--ll-border)",
                background: p.row_hover,
                borderWidth: active ? 2 : 1,
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-4 h-4 rounded-full border"
                  style={{ background: p.accent, borderColor: "rgba(0,0,0,0.1)" }}
                />
                <span className="text-xs font-medium capitalize">{key}</span>
                {active && <Check size={12} className="ml-auto text-[var(--ll-ink)]" />}
              </div>
              <div className="mt-1 text-[10px] text-[var(--ll-ink-soft)] uppercase tracking-widest">
                {p.row_hover}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom palette */}
      <div
        className="p-3 border"
        style={{
          borderColor: activePreset === "custom" ? "var(--ll-ink)" : "var(--ll-border)",
          borderWidth: activePreset === "custom" ? 2 : 1,
        }}
        data-testid="theme-custom-row"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs font-medium">Custom</div>
          <label className="flex items-center gap-1.5 text-[11px] text-[var(--ll-ink-soft)]">
            Row hover
            <input
              type="color"
              value={customHover}
              onChange={(e) => { setCustomHover(e.target.value); previewPalette(e.target.value, customAccent); }}
              onBlur={restorePalette}
              disabled={busy || !canManage}
              data-testid="theme-custom-hover-input"
              className="w-7 h-7 border cursor-pointer disabled:opacity-60"
              style={{ borderColor: "var(--ll-border)" }}
            />
            <span className="font-mono">{customHover}</span>
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-[var(--ll-ink-soft)]">
            Accent
            <input
              type="color"
              value={customAccent}
              onChange={(e) => { setCustomAccent(e.target.value); previewPalette(customHover, e.target.value); }}
              onBlur={restorePalette}
              disabled={busy || !canManage}
              data-testid="theme-custom-accent-input"
              className="w-7 h-7 border cursor-pointer disabled:opacity-60"
              style={{ borderColor: "var(--ll-border)" }}
            />
            <span className="font-mono">{customAccent}</span>
          </label>
          <button
            type="button"
            onClick={applyCustom}
            disabled={busy || !canManage}
            data-testid="theme-custom-apply"
            className="ml-auto text-xs px-3 py-1.5 bg-[var(--ll-ink)] text-white hover:bg-[var(--ll-ink-hover)] inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Apply custom
          </button>
        </div>
      </div>

      {!canManage && (
        <p className="text-[11px] text-[var(--ll-ink-soft)] mt-3">
          Only owners and admins can change the workspace theme.
        </p>
      )}
    </div>
  );
}
